import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment';
import { useParams } from 'react-router';
import { Lightbox } from 'react-modal-image';
import Skeleton from '@material-ui/lab/Skeleton';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import Markdown from 'react-markdown';
import {
  Box,
  Link,
  Typography,
  makeStyles,
  Chip,
  Divider,
  Avatar,
  Button
} from '@material-ui/core';
import { useSnackbar } from 'notistack';

function senderSelector(state, handoff, message, botId) {
  switch (message.role) {
    case 'BOT':
      return {
        avatar: '/static/images/avatars/avatar_7.png',
        name: 'bot',
        type: 'user'
      };
    case 'USER':
      return {
        avatar: '',
        name: 'Me',
        type: 'contact'
      };
    case 'APP':
      return {
        avatar: '',
        name: 'Notification',
        type: 'contact'
      };
    case 'AGENT':
      return {
        avatar: '',
        name: 'Agent',
        type: 'contact'
      };

    default:
      return {
        avatar: '',
        name: 'User',
        type: 'contact'
      };
  }
}

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: '-16px',
    display: 'flex'
  },
  botMessage: {
    borderRadius: 13,
    borderTopLeftRadius: 0
  },
  userMessage: {
    marginTop: theme.spacing(2),
    borderRadius: 13,
    borderTopRightRadius: 0
  },
  avatar: {
    height: 32,
    width: 32
  },
  image: {
    cursor: 'pointer',
    height: 'auto',
    maxWidth: '100%',
    width: 380
  },
  list: {
    '&:hover': {
      backgroundColor: '#dadada !important'
    }
  },
  markdown: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.95rem',
    lineHeight: 1.6,
    '& p': {
      marginBottom: theme.spacing(2),
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      marginBottom: theme.spacing(2),
      fontWeight: theme.typography.fontWeightBold,
      fontFamily: theme.typography.fontFamily,
    },
    '& ul, & ol': {
      paddingLeft: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    '& a': {
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold,
      fontFamily: theme.typography.fontFamily,
      fontSize: '1rem',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    '& code': {
      backgroundColor: theme.palette.background.default,
      padding: theme.spacing(0.5, 1),
      borderRadius: theme.shape.borderRadius,
      fontSize: '0.85em',
    },
    '& pre': {
      backgroundColor: theme.palette.background.default,
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      overflowX: 'auto',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
      },
    },
    '& blockquote': {
      borderLeft: `4px solid ${theme.palette.divider}`,
      paddingLeft: theme.spacing(2),
      marginLeft: 0,
      marginBottom: theme.spacing(2),
      fontStyle: 'italic',
    },
  },
}));

function Message({
  className,
  isLast,
  onAdd,
  config,
  handoff,
  message,
  sending,
  ...rest
}) {
  const classes = useStyles();
  const { botId, userId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [openedFile, setOpenedFile] = useState(null);
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const boldRegex = /(\*[^*]+\*)/g;
  const handleSend = async text => {
    try {
      if (!text || !userId) {
        return;
      }
      await onAdd(botId, text);
    } catch (error) {
      enqueueSnackbar('Error!', {
        variant: 'error'
      });
    }
  };

  const renderText = inputText => {
    return (
      <Typography
        style={{ whiteSpace: 'pre-wrap' }}
        color='inherit'
        variant='body1'
      >
        {inputText.split(urlRegex).map((urlPart, urlKey) =>
          urlRegex.test(urlPart) ? (
            <a href={urlPart} target='_parent' key={urlKey}>
              {urlPart}
            </a>
          ) : (
            urlPart
              .split(boldRegex)
              .map((textPart, textKey) =>
                boldRegex.test(textPart) ? (
                  <b key={textKey}>{textPart.slice(1, -1)}</b>
                ) : (
                  <span key={textKey}>{textPart}</span>
                )
              )
          )
        )}
      </Typography>
    );
  };

  const openInNewTab = url => {
    const newTab = window.open(url, '_blank', 'noopener,noreferrer');
    if (newTab) newTab.opener = null;
  };

  const renderMessage = payload => {
    switch (payload.type) {
      case 'image':
        return (
          <Box mt={2} onClick={() => openInNewTab(payload.url)}>
            <img alt='Attachment' className={classes.image} src={payload.url} />
            {payload.caption && (
              <Typography color='inherit' variant='body1'>
                {payload.caption}
              </Typography>
            )}
          </Box>
        );
      case 'audio':
        return (
          <audio controls>
            <source src={payload.url} type={payload.mimeType} />
          </audio>
        );
      case 'voice':
        return (
          <audio controls>
            <source src={payload.url} type={payload.mimeType} />
          </audio>
        );
      case 'video':
        return (
          <video controls>
            <source src={payload.url} type={payload.mimeType} />
          </video>
        );
      case 'text':
        const renderers = {
          link: ({node, ...props}) => <a target="_parent" {...props} />
        };
        return (
          <Box className={classes.markdown}>
            <Markdown renderers={renderers}>{payload.text}</Markdown>
          </Box>
        );

      case 'document':
        return (
          <Link
            color='inherit'
            component={RouterLink}
            to={{
              pathname: payload.url
            }}
            variant='h6'
            target='_blank'
          >
            {payload.filename}
          </Link>
        );
      case 'location':
        return (
          <Typography color='inherit' variant='body1'>
            {payload.name && (
              <>
                <Link
                  color='inherit'
                  component={RouterLink}
                  to={{
                    pathname: payload.url
                  }}
                  variant='h6'
                  target='_blank'
                >
                  {payload.name}
                </Link>
                <br></br>
              </>
            )}
            {payload.address && (
              <>
                {payload.address}
                <br></br>
              </>
            )}
            <Link
              color='secondary'
              component={RouterLink}
              to={{
                pathname: `https://www.google.com/maps/search/?api=1&query=${payload.latitude},${payload.longitude}`
              }}
              variant='h6'
              target='_blank'
            >
              {'Open Location'}
            </Link>
          </Typography>
        );
      case 'list':
        return (
          <div>
            <Typography
              color='inherit'
              style={{ fontWeight: 'bold' }}
              variant='body1'
            >
              {payload.title}
            </Typography>
            <Typography
              style={{ marginBottom: '5px' }}
              color='inherit'
              variant='body2'
            >
              {payload.description}
            </Typography>

            {payload.items.map((e, idx) => {
              return (
                <Box mb={0.5}>
                  <Button
                    onClick={() => handleSend(e.label)}
                    variant='contained'
                    key={idx}
                    className={classes.list}
                    style={{
                      boxShadow: 'rgb(0 0 0 / 20%) 0px 2px 4px 0px',
                      borderRadius: '5px',
                      backgroundColor: 'white',
                      justifyContent: 'flex-start'
                    }}
                  >
                    <Box
                      display={'flex'}
                      justifyContent={'center'}
                      alignItems={'flex-start'}
                      flexDirection={'column'}
                    >
                      <Typography
                        style={{
                          fontSize: '14px',
                          textAlign: 'left'
                        }}
                      >
                        {e.label}
                      </Typography>
                      <Typography
                        style={{
                          fontSize: '10px',
                          fontWeight: '300',
                          textAlign: 'left'
                        }}
                      >
                        {e.description}
                      </Typography>
                    </Box>
                    <Divider />
                  </Button>
                </Box>
              );
            })}
          </div>
        );
      case 'template':
        return (
          <Typography color='inherit' variant='body1'>
            {`Template: ${payload.name}`}
          </Typography>
        );
      case 'quick_reply':
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography color='inherit' variant='subtitle1'>
              {`${payload.title}`}
            </Typography>
            <Typography color='inherit' variant='caption'>
              {payload.description}
            </Typography>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: '8px', 
                marginTop: '5px'
              }}
            >
              {payload.buttons.map((button, idx) => {
                if (button.type === 'web_url') {
                  return (
                    <a
                      key={idx}
                      href={button.value}
                      target='_blank'
                      rel='noopener noreferrer'
                      className={classes.list}
                    >
                      <Chip
                        deleteIcon={<OpenInNewIcon fontSize='small' />}
                        onDelete={() => {}}
                        clickable
                        key={idx}
                        style={{
                          marginRight: '5px',
                          backgroundColor: 'white',
                          color: 'black',
                          boxShadow: 'rgb(0 0 0 / 20%) 0px 2px 4px 0px',
                          minWidth: '75px',
                          textAlign: 'center'
                        }}
                        className={classes.list}
                        label={button.label}
                      />
                    </a>
                  );
                } else {
                  return (
                    <Chip
                      clickable
                      key={idx}
                      onClick={() => handleSend(button.label)}
                      className={classes.list}
                      style={{
                        marginRight: '5px',
                        backgroundColor: 'white',
                        color: 'black',
                        boxShadow: 'rgb(0 0 0 / 20%) 0px 2px 4px 0px',
                        minWidth: '75px',
                        textAlign: 'center'
                      }}
                      label={button.label}
                    />
                  );
                }
              })}
            </div>
          </div>
        );
      default:
        return (
          <Typography color='inherit' variant='body1'>
            {`usupported message`}
          </Typography>
        );
    }
  };
  let bgStyle = config.bgColor || '#242b39';
  let mlStyle = 'auto';
  let colorStyle = config.textColor;
  let textAlignStyle = 'auto';
  let shadowStyle = '1';
  let avatar = config.avatar || '';

  if (message.role !== 'USER') {
    bgStyle = 'white';
    mlStyle = 0;
    colorStyle = 'black';
  }

  if (message.role === 'APP') {
    bgStyle = 'transparent';
    textAlignStyle = 'center';
    shadowStyle = 'transparent';
    colorStyle = 'lightgray';
    mlStyle = '25%';
  }
  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Box
        display='flex'
        maxWidth={500}
        ml={mlStyle} // sender role
      >
        <Box>
          {message.role !== 'USER' && (
            <Avatar style={{ width: 32, height: 32 }} src={avatar} />
          )}
          <Box
            textAlign={textAlignStyle}
            style={{ color: colorStyle, backgroundColor: bgStyle }}
            py={1}
            px={2}
            className={
              message.role !== 'USER' ? classes.botMessage : classes.userMessage
            }
            boxShadow={shadowStyle}
          >
            {sending && isLast ? (
              <Skeleton variant='text' />
            ) : (
              <Box mt={1}>{renderMessage(message.payload)}</Box>
            )}
          </Box>
          <Box mt={1} display='flex' justifyContent='flex-end'>
            <Typography noWrap color='textSecondary' variant='caption'>
              {message.role === 'APP' || message.pending
                ? ''
                : moment(message.created_at).fromNow()}{' '}
              {message.pending ? message.status : ''}
            </Typography>
          </Box>
        </Box>
      </Box>

      {openedFile && (
        <Lightbox large={openedFile} onClose={() => setOpenedFile(null)} />
      )}
    </div>
  );
}

Message.propTypes = {
  className: PropTypes.string,
  message: PropTypes.object.isRequired
};

export default Message;